"use client";

import React, {
  useCallback,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState,
  ViewTransition,
} from "react";
import { cn } from "utils/utils";
import { EndOfListCard } from "modules/common/components/EndOfListCard";
import { useIsomorphicLayoutEffect } from "modules/common/hooks/useIsomorphicLayoutEffect";
import { useViewTransitionName } from "modules/common/hooks/useViewTransitionName";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useFetchNextPageOnReach } from "modules/common/hooks/useFetchNextPageOnReach";
import { CardSkeleton } from "modules/common/components/CardSkeleton";
import {
  END_OF_LIST_KEY,
  staggerBatchFor,
  staggerBatchLive,
  type StaggerBatch,
} from "modules/common/components/grids/staggerBatches";

// How close (in px) the end of the list gets to the screen before the next page
// loads. This is literally how much list is left below the fold when the fetch
// fires, so it is the budget for the request to land before the user scrolls
// into empty space — one card row (~455px) was not enough and read as a stall.
//
// Bounded by the height of one page: once the lookahead exceeds what a page
// adds, the sentinel is still in range when that page lands and the next one
// fetches straight away. Self-limiting rather than a runaway — each page pushes
// the sentinel down — but it fetches before the user has scrolled.
//
// The comparison is against `window.innerHeight + margin`, so the real headroom
// is (page height - viewport height) and a tall screen eats most of it. At
// BROWSE_PAGE_SIZE = 30 a page is ~4 rows, and row height falls with the column
// count (wider viewports mean narrower, shorter cards), so the tightest case is
// the widest breakpoint at ~1200-1500px per page rather than the ~1820px a
// laptop sees. 1200 and 1400 were both tried and both pulled extra pages in
// before any scrolling.
// In viewport heights: 1.5 screens of runway lets most appends resolve
// off-screen (so the stagger/skeleton race stays a slow-network fallback)
// while a standard desktop page (~3+ screens tall) still never fetches on
// first render.
const NEXT_PAGE_MARGIN_SCREENS = 1.5;

// Calculate columns based on width
function getColsForWidth(width: number): number {
  if (width >= 1920) return 9;
  if (width >= 1560) return 7;
  if (width >= 1280) return 5;
  if (width >= 960) return 4;
  if (width >= 600) return 3;
  return 2;
}
export interface ResponsiveCardDisplaySection<T> {
  key: string;
  items: T[];
  header?: React.ReactNode;
}

/**
 * The section a page landed in: the one that gained items between two renders
 * of the same list. The section that gained the MOST, so that a page split
 * across sections (the proposal picker puts a partly spoken-for card in both)
 * names the block the user is really watching fill.
 *
 * Undefined when there is nothing to compare or nothing grew.
 */
function grownSectionKey<T>(
  previous: ResponsiveCardDisplaySection<T>[] | undefined,
  next: ResponsiveCardDisplaySection<T>[] | undefined
): string | undefined {
  if (!previous || !next) return undefined;
  let key: string | undefined;
  let best = 0;
  for (const section of next) {
    const before =
      previous.find((candidate) => candidate.key === section.key)?.items
        .length ?? 0;
    const gained = section.items.length - before;
    if (gained > best) {
      best = gained;
      key = section.key;
    }
  }
  return key;
}

type LayoutRow<T> =
  | { type: "header"; key: string; content: React.ReactNode }
  | {
      type: "items";
      key: string;
      sectionKey: string;
      entries: Array<{ item: T; index: number }>;
    };

interface ResponsiveCardDisplayGridProps<T> {
  // Data and loading states
  data: T[] | undefined;
  isLoading: boolean;
  /**
   * Keep committed rows mounted while a replacement result set loads. This
   * preserves per-card view-transition identity across searches, sorts, and
   * facets. Callers should expose the pending state outside the grid.
   */
  retainRowsWhileLoading?: boolean;
  /** Reports the complete replacement handoff, including the card transition
   * after a cached result set is already available. */
  onReplacementPendingChange?: (isPending: boolean) => void;

  // Rendering
  renderItem: (item: T, index: number, cols: number) => React.ReactNode;
  renderSkeleton?: () => React.ReactNode;
  skeletonRowCount?: number;
  /**
   * Must key on the ITEM alone. The `index` is passed for convenience but is
   * not the same number everywhere: the render path counts over `sections`
   * (display order) while the append check below counts over `data` (source
   * order), so a key that varies with it reads every page load as a re-key and
   * throws the user back to the top of the list.
   */
  getItemKey: (item: T, index: number) => string | number;
  /** Optional visual sections. Items must also be present in `data`. */
  sections?: ResponsiveCardDisplaySection<T>[];
  /**
   * Identity of the result set — the search/sort/facet tuple that forms the
   * query key. Scroll resets to the top only when this CHANGES, which is the
   * only thing that means "the user is looking at a different list".
   *
   * Without it the component has to infer that from the rows, and it cannot:
   * "the incoming rows are not a prefix of mine" is equally true for a facet
   * change and for a card being deleted from the middle of the list, so
   * editing one card throws the user back to the top of a list they were 150
   * items into. Omitted, that inference is what you get.
   */
  listKey?: string;

  // Infinite scroll - replacing loadMoreRef with these props
  fetchNextPage: () => void;
  hasNextPage: boolean;
  /**
   * React Query's own next-page flag. Required rather than optional: it
   * re-entrancy-guards the auto-fetch below, so a caller that omitted it
   * would fetch every page in a loop whenever the virtualizer renders the
   * whole (short) list.
   */
  isFetchingNextPage: boolean;

  // Customization
  emptyMessage?: string;
  emptyDescription?: string;
  className?: string;
  containerClassName?: string;

  /**
   * Animate rows that changed WITHOUT the listKey changing — a card edited or
   * removed mid-list, which on a quantity/price sort also moves it. Off by
   * default: for a list whose rows are rewritten in place by something other
   * than the user (the picker's availability overlay, its section re-split)
   * that is constant motion nobody asked for. Turn it on for the lists users
   * actually edit — the collection and their trade lists.
   */
  animateEdits?: boolean;

  // End of list card (shown when all items loaded)
  showEndOfList?: boolean;
  endOfListMessage?: string;
  endOfListDescription?: string;
}

export function ResponsiveCardDisplayGrid<T>({
  data,
  isLoading,
  retainRowsWhileLoading = false,
  onReplacementPendingChange,
  renderItem,
  renderSkeleton,
  skeletonRowCount,
  getItemKey,
  sections,
  listKey,
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage,
  emptyMessage = "No items found",
  emptyDescription = "Try adjusting your filters or search criteria or adding cards",
  className,
  containerClassName,
  animateEdits = false,
  showEndOfList = false,
  endOfListMessage = "You've seen it all!",
  endOfListDescription = "Check back later for new additions",
}: ResponsiveCardDisplayGridProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollReset = useRef(false);
  // Whether the last commit showed the end-of-list card (see END_OF_LIST_KEY).
  const endCardShownRef = useRef(false);
  // Kept in a ref rather than read during render: the visible-window check
  // below runs on every scroll-driven virtualizer render, and a clientHeight
  // read there forces a synchronous layout on each of those frames. Written on
  // mount and on resize, which is all that can change it.
  const viewportHeight = useRef(0);
  const attachScrollContainer = useCallback((el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    viewportHeight.current = el?.clientHeight ?? 0;
    // A result-key change can temporarily swap the scrolling grid for its
    // loading skeleton. In that gap there is no element for the reset effect
    // to scroll, and React may reuse the skeleton DOM node (including its old
    // scrollTop) when the replacement rows mount. Reset the DOM node as soon
    // as it exists; the virtualizer completes the reset after it has attached.
    if (el && pendingScrollReset.current) {
      el.scrollTo({ top: 0 });
    }
  }, []);
  const lastKnownWidthRef = useRef<number>(0);
  const [cols, setCols] = useState(2);
  // Rows are absolutely positioned and chunked by `cols`, so rendering them
  // before the container is measured lays them out at the initial 2 columns —
  // on a server-prefetched page that wrong layout is what the browser paints
  // until hydration. Skeletons (plain CSS grid + container queries) are
  // correct at any width, so show those until the first measurement lands.
  const [measured, setMeasured] = useState(false);

  // React Query updates aren't Transitions, so <ViewTransition> would never
  // activate on them — but only RE-KEYED lists (sort/filter/search) should
  // animate. Infinite-scroll appends must NOT run a view transition: snapshot
  // layers paint at the document root, unclipped by the scroll container, so
  // overscan cards visibly overflow it while the animation runs. (A
  // useDeferredValue here is wrong for the same reason: its catch-up render
  // is itself a Transition, so appends animate anyway — and it starves under
  // back-to-back page loads, which made the auto-fetch guard below loop.)
  // So: hold the rendered list in state; appends (old list is a prefix of the
  // new one) adopt synchronously during render, re-keys adopt inside an
  // explicit startTransition, which is what activates the animations.
  //
  // The append bookkeeping — which section the last page extended, and the
  // stagger boundary — rides along IN the same state rather than in refs. Refs
  // written during render survive a render React throws away (a concurrent
  // interrupt, StrictMode's double invoke) even though the state that render
  // computed does not, so the two drift apart; carried here they are set by the
  // same update or not at all.
  const [list, setList] = useState<{
    data: T[] | undefined;
    sections: ResponsiveCardDisplaySection<T>[] | undefined;
    /** Result-set identity currently committed to the DOM. */
    key: string | undefined;
    /** Key of the rows surface. Trails `key` on a carry-over (a facet change
     *  whose query keeps the previous pages as placeholder data): the rows
     *  are unchanged, so remounting the surface would only tear down every
     *  card node and restart the lazy images below the fold — which read as
     *  rows three onward flickering before the real result set landed. Also
     *  trails `key` after a key change that committed with no rows, for the
     *  same reason: the effect reads `surfaceKey !== key` as "this key's
     *  first rows are still owed". */
    surfaceKey: string | undefined;
    /** Section an arriving page was last seen to extend. */
    growKey: string | undefined;
    /** Batches of cards dealing in with the staggered CSS entrance (see
     *  global.css), oldest first; a card belongs to the newest live batch
     *  whose `start` is at or below its index and that did not carry it over
     *  (see staggerBatchFor). A list rather than one boundary
     *  because page two lands while page one is still dealing in — moving a
     *  single boundary stripped the class from page one mid-animation, which
     *  cancels it and snaps every card to opaque at once. Batches older than
     *  the window are dropped on the next update. */
    stagger: StaggerBatch[];
  }>({
    data,
    sections,
    key: listKey,
    surfaceKey: listKey,
    growKey: undefined,
    stagger: [],
  });
  const listData = list.data;
  const listSections = list.sections;
  const renderedListMatchesResult =
    listKey === undefined || list.key === listKey;

  // A full prefix check, not just the first and last key: equal length with
  // unchanged endpoints (a sort that happens to keep them, an item moving
  // between sections) is a reorder, and a reorder must animate rather than be
  // adopted urgently — otherwise the cards snap, which is exactly what the
  // transitions below exist to prevent.
  //
  // Short-circuited on prop identity first: the check is O(n) in `getItemKey`,
  // and `getItemKey` is not cheap (tradeItemKey sorts and JSON.stringifies the
  // id array), so running it twice per item on every scroll-driven virtualizer
  // render costs tens of thousands of allocations a frame on a list that has
  // paged up to a few thousand cards. Nothing can have appended when the props
  // are the very objects already in state.
  const listChanged = data !== listData || sections !== listSections;
  const isAppend =
    listChanged &&
    list.key === listKey &&
    !!data &&
    !!listData &&
    listData.length > 0 &&
    data.length >= listData.length &&
    listData.every(
      (item, index) =>
        getItemKey(data[index], index) === getItemKey(item, index)
    );

  if (isAppend) {
    // Render-phase adoption: commits in this same urgent render, no Transition.
    // Everything derived from the append is computed from `previous` inside the
    // updater, so a discarded render leaves nothing behind.
    setList((previous) => ({
      data,
      sections,
      key: previous.key,
      surfaceKey: previous.surfaceKey,
      // Which section this page actually extended, measured rather than
      // guessed from the end of the list — see `growSectionKey` below.
      growKey: grownSectionKey(previous.sections, sections) ?? previous.growKey,
      // The boundary is an index into `data` (source order), while the `index`
      // handed to renderItem counts over `sections` (display order). Those
      // agree only while there is at most one section — with two or more, an
      // arriving page extends each of them at its own end, so a single index
      // would land the stagger on whichever cards happen to sit past that
      // display position rather than on the ones that just arrived.
      stagger:
        (sections?.length ?? 0) <= 1 &&
        data.length > (previous.data?.length ?? 0)
          ? [
              ...previous.stagger.filter(staggerBatchLive),
              { start: previous.data?.length ?? 0, at: Date.now() },
            ]
          : previous.stagger,
    }));
  }

  useEffect(() => {
    // A changed React Query key temporarily supplies no rows. Keep the
    // committed cards mounted so matching cards retain their view-transition
    // identity when the replacement page arrives.
    if (retainRowsWhileLoading && isLoading && listData?.length) return;

    // Bail BEFORE scheduling: appends were already adopted during render, so
    // reaching startTransition here means a genuine re-key. Scheduling an
    // (even self-bailing) transition on every data change is not free — React
    // can't eagerly drop it while other state is pending, so concurrent
    // scroll-driven virtualizer updates get batched into a transition commit
    // and every card leaving the window runs its exit animation.
    if (
      listData === data &&
      listSections === sections &&
      list.key === listKey
    ) {
      // Second reset, now that the new cards are the ones on screen. The reset
      // below fires while the previous cards are still mounted, and the browser's
      // scroll anchoring pulls the offset part-way back as they are replaced.
      if (pendingScrollReset.current) {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: 0 });
        }
      }
      return;
    }
    // Reaching here means the rows are not a prefix of mine. That is NOT on its
    // own a reason to animate, and not a reason to scroll: it is equally true of
    // a facet change and of one card being edited or removed mid-list. Only a
    // changed listKey means the user asked for a different list, and so belongs
    // at the top of it — the committed grid state keeps the previous cards
    // mounted, which also keeps the old scroll offset unless we reset it.
    const identityChanged = listKey === undefined || list.key !== listKey;
    if (identityChanged) {
      pendingScrollReset.current = true;
      scrollContainerRef.current?.scrollTo({ top: 0 });
    }
    const carriedOver =
      identityChanged && data === listData && sections === listSections;
    // Rows the current key is still owed, derived rather than remembered:
    // `surfaceKey` trails `key` after a carry-over commit (the rows on screen
    // are the old ones) and after a rows-less key-change commit (there are no
    // rows at all), and the commit below only advances it when this key's
    // rows land. So the rows arriving next are this key's FIRST and animate
    // even though the identity change was consumed a commit ago — including
    // when they arrive empty, which is the zero-result swap. The initial
    // state has the two equal, so a first load is not a re-key.
    //
    // This was a ref, and a ref could not be cleared: structural sharing hands
    // back reference-identical rows on a carry-over, the effect bails above
    // before consuming the flag, and it stayed set until some later same-key
    // edit transitioned a grid whose caller never asked for that.
    const rowsOwed = list.surfaceKey !== list.key;
    const rekey = identityChanged || rowsOwed;
    const commit = () => {
      // Every card new to the list deals in with the plain-CSS stagger — the
      // one entrance for appends, a grid mounting from empty, and re-keys
      // alike (the per-card <ViewTransition> enter is "none": on a re-key the
      // rows surface is a fresh node, so React could not run it there anyway,
      // and the surface rise-in read as a pop next to the staggered rows).
      // Cards carried over by key are skipped: they morph via their shared
      // boundary and would blink if the live element also faded in. The
      // delay counts from the first new card so a single insertion is not
      // held for its list position; sections carry their own order, so only
      // the single-section layout can compute that (same rule as the append
      // stagger above). The grow key goes — the new list's sections have
      // their own shape.
      // One pass of `getItemKey` over the committed list. That is not cheap
      // (tradeItemKey sorts and JSON.stringifies the id array), but it is paid
      // once per RE-KEY inside this commit — never per frame, unlike the
      // isAppend prefix check above — and the findIndex below short-circuits at
      // the first new card, which on a re-key is normally index 0.
      const previousKeys = new Set<string | number>(
        (listData ?? []).map((item, index) => getItemKey(item, index))
      );
      if (endCardShownRef.current) previousKeys.add(END_OF_LIST_KEY);
      const firstNewCard =
        data?.findIndex(
          (item, index) => !previousKeys.has(getItemKey(item, index))
        ) ?? -1;
      // A re-key drops the earlier batches: their indices belong to the old
      // list. A same-key change keeps the ones still dealing in — and so does a
      // carry-over, where the rows on screen are still the OLD list's, at the
      // old indices. Dropping them there strips `stagger-card-in` from cards
      // mid-animation, which with `fill: both` snaps them from invisible to
      // opaque: exactly the single-boundary bug the batch list exists to avoid.
      setList((previous) => {
        const batches =
          identityChanged && !carriedOver
            ? []
            : previous.stagger.filter(staggerBatchLive);
        return {
          data,
          sections,
          key: listKey,
          // Advances only when this key's rows land. A carry-over keeps the
          // old rows mounted under the old surface key (remounting them would
          // restart every lazy image); a rows-less commit has no surface to
          // key at all. Either way `surfaceKey !== key` is what tells the
          // effect above that the rows arriving next are owed a re-key.
          surfaceKey:
            carriedOver || !data?.length ? previous.surfaceKey : listKey,
          growKey: undefined,
          stagger:
            firstNewCard === -1
              ? batches
              : [
                  ...batches,
                  {
                    start: (sections?.length ?? 0) <= 1 ? firstNewCard : 0,
                    at: Date.now(),
                    skip: previousKeys,
                  },
                ],
        };
      });
    };
    // No animation while a modal is open. The view transition layer paints
    // after ALL document content "including any content rendered in the top
    // layer", and an open <dialog> is captured INTO the `root` snapshot — so
    // every named card group is a sibling painted above it, and the cards
    // animate on top of the filter modal that triggered the re-key. (The
    // mobile facet modal applies filters live, so this is the normal path
    // there, not an edge case.) Nothing is lost: the list is behind the modal,
    // so there is no animation for the user to see.
    //
    // Same for rows that changed without the list changing — the proposal
    // picker rewrites items in place (picking a card moves it between sections,
    // and the availability overlay rewrites earlier pages when their
    // availability lands), which is not a prefix but is also not a new list.
    // Transitioning those snapshots the rows at the document root, unclipped by
    // the scroll container, so pages scrolled out of view paint over the layout
    // above it. Only a genuine re-key gets to animate — unless the caller opted
    // in, which the collection and trade list grids do: there an in-place row
    // change is the user's own edit, and watching the rest of the list close
    // ranks around it is the whole point.
    // A carry-over changes nothing on screen, so it commits urgently too: a
    // transition there would only delay the real rows behind a no-op.
    //
    // And no animation at all on a backgrounded tab. A binder left open in
    // another tab still re-keys on websocket-driven refetches, and every engine
    // skips a transition started while the document is hidden — rejecting
    // `ready` with an InvalidStateError whose wording React only recognises for
    // Chrome and Firefox, so Safari's ("Skipped ViewTransition due to document
    // being hidden") escapes its filter and is reported as a recoverable error.
    // The commit itself is unaffected; nobody is watching the animation.
    if (
      (!rekey && !animateEdits) ||
      carriedOver ||
      document.hidden ||
      document.querySelector("dialog[open]")
    ) {
      commit();
      return;
    }
    startTransition(commit);
  }, [
    data,
    sections,
    listData,
    listSections,
    listKey,
    list.key,
    list.surfaceKey,
    animateEdits,
    isLoading,
    retainRowsWhileLoading,
    getItemKey,
  ]);

  const vtName = useViewTransitionName("card");

  // Measure container width synchronously before paint to avoid skeleton/card flash
  useIsomorphicLayoutEffect(() => {
    const currentRef = containerRef.current;
    if (!currentRef) return;

    const updateCols = (width: number) => {
      if (width > 0) {
        lastKnownWidthRef.current = width;
        setCols(getColsForWidth(width));
        setMeasured(true);
      } else if (lastKnownWidthRef.current > 0) {
        setCols(getColsForWidth(lastKnownWidthRef.current));
        setMeasured(true);
      }
    };

    const measure = () => {
      viewportHeight.current = scrollContainerRef.current?.clientHeight ?? 0;
      updateCols(currentRef.clientWidth);
    };

    // Initial measurement
    measure();

    // Watch for resize to keep colsRef accurate for priority hints
    const resizeObserver = new ResizeObserver((entries) => {
      viewportHeight.current = scrollContainerRef.current?.clientHeight ?? 0;
      for (const entry of entries) {
        updateCols(entry.contentRect.width);
      }
    });
    resizeObserver.observe(currentRef);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // `isLoading` goes false the moment the first page lands, but the rows are
  // adopted in a passive-effect transition that commits AFTER that paint — so
  // with no previous rows to keep showing we'd flash "no items" for a frame.
  const awaitingFirstRows = !listData?.length && !!data?.length;
  const awaitingReplacementRows =
    retainRowsWhileLoading &&
    !!listData?.length &&
    (isLoading ||
      (listChanged && (listKey === undefined || list.key !== listKey)));
  useEffect(() => {
    onReplacementPendingChange?.(awaitingReplacementRows);
  }, [awaitingReplacementRows, onReplacementPendingChange]);
  useEffect(
    () => () => onReplacementPendingChange?.(false),
    [onReplacementPendingChange]
  );
  // `isLoading` flips in the caller's urgent render, so it must not unmount
  // the rows by itself: the skeleton waits for the committed list to empty,
  // which the effect above does inside startTransition on a key change — that
  // commit is where the rows surface fades out and the skeleton fades in.
  //
  // This is weaker than the `retainRowsWhileLoading` gate it replaced, and
  // deliberately so. Callers that retain rows are unaffected: they never commit
  // the empty list. Callers that do NOT retain them (CardsContainer changing a
  // facet) now keep the previous rows until that transition commit instead of
  // cutting to the skeleton — which is the crossfade, not a stale-data window.
  // If a caller ever needs the hard cut back, that is a new prop, not a change
  // here.
  const showSkeleton =
    (isLoading && !listData?.length) || !measured || awaitingFirstRows;
  const isEmpty = !showSkeleton && !listData?.length;

  const layoutRows = useMemo<LayoutRow<T>[]>(() => {
    const sourceSections = listSections ?? [
      { key: "default", items: listData ?? [], header: undefined },
    ];
    const rows: LayoutRow<T>[] = [];
    let globalIndex = 0;

    for (const section of sourceSections) {
      if (section.items.length === 0) continue;
      if (section.header) {
        rows.push({
          type: "header",
          key: `${section.key}-header`,
          content: section.header,
        });
      }
      for (let start = 0; start < section.items.length; start += cols) {
        rows.push({
          type: "items",
          key: `${section.key}-${start / cols}`,
          sectionKey: section.key,
          entries: section.items.slice(start, start + cols).map((item) => ({
            item,
            index: globalIndex++,
          })),
        });
      }
    }

    return rows;
  }, [cols, listData, listSections]);

  const lastItemRowIndex = layoutRows.findLastIndex(
    (row) => row.type === "items"
  );
  const lastItemRow = layoutRows[lastItemRowIndex];
  // Which section an arriving page actually extends. With sections the end of
  // the LIST is not where new cards land — the picker's available block sits
  // above its active-proposal block, so a page lands in the middle and
  // skeletons put at the bottom sit under a section the user then watches get
  // pushed down.
  //
  // Taken from the last append that was actually observed (`list.growKey`),
  // because there is no way to read it off the current list: "the section
  // holding the last item of `data`" picks the picker's active-proposal block
  // whenever the last source item is partly spoken for, which is exactly the
  // section that does not grow. Before the first append lands there is no
  // observation to use, so that guess is still the fallback — it is only ever
  // one page wrong, and only on a list whose sections split the tail.
  const growSectionKey =
    // Ignored once that section has emptied out — it names no rendered row, and
    // the skeletons would have nowhere to go.
    (listSections?.some(
      (section) => section.key === list.growKey && section.items.length > 0
    )
      ? list.growKey
      : undefined) ??
    listSections?.find((section) => section.items.at(-1) === listData?.at(-1))
      ?.key ??
    (lastItemRow?.type === "items" ? lastItemRow.sectionKey : undefined);
  const appendRowIndex = layoutRows.findLastIndex(
    (row) => row.type === "items" && row.sectionKey === growSectionKey
  );
  const appendRow = layoutRows[appendRowIndex];

  // "You've seen it all" belongs at the end of the section pages arrive in, not
  // at the end of the LIST: in the proposal picker the available cards grow
  // above a fixed active-proposal block, so the bottom of the list is a section
  // that never grows and a card there reads as the end of the wrong thing.
  // Without sections the two are the same row.
  const showEndOfListCard =
    showEndOfList && !hasNextPage && listData && listData.length > 0;
  const hasRoomInLastRow =
    showEndOfListCard &&
    appendRow?.type === "items" &&
    appendRow.entries.length < cols;
  const needsEndOfListRow = showEndOfListCard && !hasRoomInLastRow;
  // What the last commit put on screen, for the next commit's skip set. This
  // effect is declared after the commit effect, so it runs after it.
  useEffect(() => {
    endCardShownRef.current = !!showEndOfListCard;
  });
  // Virtual index of the inserted row, and the shift it puts on every layout
  // row after it. -1 when there is no inserted row, so the shift is a no-op.
  const endRowIndex = needsEndOfListRow ? appendRowIndex + 1 : -1;
  const toLayoutIndex = (index: number) =>
    endRowIndex >= 0 && index > endRowIndex ? index - 1 : index;

  // Next-page skeletons fill that section's last row to the row edge rather
  // than sitting in it as a single card: a partial row already reads as
  // unfinished, and topping it up keeps the grid a complete rectangle for the
  // length of the fetch. If the row is already full there is no gap to fill, so
  // a whole row of them opens below — but only when that section is the last
  // one, since a flowed row cannot open in the middle of the list.
  //
  // Never collides with the end-of-list card: this needs isFetchingNextPage,
  // which implies hasNextPage, and that card requires !hasNextPage.
  // A replacement query may resolve before its rows commit because that commit
  // is a Transition. Its pagination state must not operate on the retained rows
  // from the outgoing query in that gap.
  const isFetchingRenderedNextPage =
    isFetchingNextPage && renderedListMatchesResult;
  const lastRowSkeletons =
    isFetchingRenderedNextPage && appendRow?.type === "items"
      ? cols - appendRow.entries.length
      : 0;
  const needsSkeletonRow =
    isFetchingRenderedNextPage &&
    lastRowSkeletons === 0 &&
    appendRowIndex === lastItemRowIndex;
  const measuredRowHeight = useRef(380);
  // Until a real row has been measured, getTotalSize() is `380 * rows` while
  // real rows run 430-700px — so the sentinel (positioned at getTotalSize())
  // sits hundreds of px too high on the first commit after the skeleton swap.
  // The fetch check runs on that commit and would read "the end is on screen"
  // for a list that in fact ends well below the fold, loading page 2 before
  // the user has scrolled. Decide on measured geometry only.
  const [rowsMeasured, setRowsMeasured] = useState(false);

  const rowVirtualizer = useVirtualizer({
    count: layoutRows.length + (needsEndOfListRow ? 1 : 0),
    getScrollElement: () => scrollContainerRef.current,
    // Item-row estimate tracks the last measured row: the literal 380 is only a
    // pre-measure fallback. Real rows vary with viewport (~430-700px), and a
    // wrong estimate makes getTotalSize() jump as rows get measured, which
    // reads as the container height oscillating during load/scroll.
    estimateSize: (index) =>
      index !== endRowIndex &&
      layoutRows[toLayoutIndex(index)]?.type === "header"
        ? 56
        : measuredRowHeight.current,
    overscan: 4,
    // Lets the virtualizer produce rows on the very first render, before the
    // scroll element ref attaches — otherwise there's a one-frame empty (or
    // skeleton) state between "data arrived" and "rows visible".
    initialRect: { width: 0, height: 1000 },
  });

  useIsomorphicLayoutEffect(() => {
    if (!pendingScrollReset.current) return;
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // The virtualizer survives a result-key change, so its remembered offset
    // can otherwise be applied to the fresh keyed scroll node after the ref's
    // direct reset. Reset both owners once the replacement rows are committed.
    rowVirtualizer.scrollToOffset(0);
    scrollContainer.scrollTo({ top: 0 });
    pendingScrollReset.current = false;
  }, [list.key, listData, rowVirtualizer]);

  // Stable identity: an inline ref callback would detach/re-attach every
  // row's measurement on every render, which is pure churn while pages stream in.
  const measureItemRow = useCallback(
    (el: HTMLDivElement | null) => {
      rowVirtualizer.measureElement(el);
      if (el) {
        measuredRowHeight.current = el.getBoundingClientRect().height;
        // Re-measured on every resize/re-chunk, so the estimate stays correct
        // for this viewport's column count. React bails on the repeat `true`.
        setRowsMeasured(true);
      }
    },
    [rowVirtualizer]
  );

  // Next page loads when the end of the list nears the screen — see the hook for
  // why neither the virtualizer's window nor this container's scrollTop can
  // drive it.
  const loadMoreRef = useRef<HTMLDivElement>(null);
  useFetchNextPageOnReach(loadMoreRef, {
    // Gated on measurement, not just availability: see `rowsMeasured`. The
    // measurement itself re-renders, so the check re-runs with real geometry.
    hasNextPage: hasNextPage && rowsMeasured && renderedListMatchesResult,
    isFetchingNextPage,
    fetchNextPage,
    marginScreens: NEXT_PAGE_MARGIN_SCREENS,
    rows: listData,
  });

  const gridClassName = cn(
    "grid grid-cols-2 items-stretch gap-1",
    "@min-[600px]:grid-cols-3",
    "@min-[960px]:grid-cols-4",
    "@min-[1280px]:grid-cols-5",
    "@min-[1560px]:grid-cols-7",
    "@min-[1920px]:grid-cols-9",
    className
  );

  return (
    // Single stable wrapper so containerRef always points to the same DOM element,
    // keeping the ResizeObserver attached across loading/empty/data state changes.
    // w-full ensures clientWidth resolves correctly on first measurement.
    <div
      ref={containerRef}
      className={cn(
        "@container relative flex h-full w-full flex-col",
        containerClassName
      )}
      aria-busy={awaitingReplacementRows}
    >
      {/* Skeleton <-> rows swap. The re-key commit runs inside startTransition
          (also on the cold path, via the trailing surfaceKey), so these surface
          boundaries activate: the skeleton fades out and the rows surface
          rises in, with the cards' CSS deal-in showing through the live new
          snapshot. `default="none"` keeps the wrappers unnamed during card
          re-keys, where the per-card boundaries are the animation. Both
          blocks clip their own content and are flex-1 in the panel, so their
          snapshots stay inside their boxes (ancestor clips never apply to
          snapshots — that was the August "grid overflows" flash, from
          append transitions that no longer exist). */}
      {showSkeleton && (
        <ViewTransition
          key="skeleton"
          enter="vt-surface-enter"
          exit="vt-surface-exit-hold"
          default="none"
        >
          <div className="flex-1 overflow-hidden">
            {/* Padding mirrors the rows below (container py-2 md:py-0, rows
              px-1): this skeleton is what the streamed SSR HTML paints before
              hydration, so any difference is a visible pre-hydration shift. */}
            <div className={cn(gridClassName, "gap-2 px-1 py-2 md:py-0")}>
              {Array.from({
                length: listData?.length || (skeletonRowCount ?? 2) * 9,
              }).map((_, i) => (
                // Plain CSS, not a view transition, so the tiles deal in on any
                // mount — including the urgent hint -> skeleton commit from the
                // search input, and the SSR skeleton on first paint. The 300ms
                // cap is --vt-surface-hold in global.css: the outgoing surface
                // stays opaque for exactly this cadence before it fades.
                <div
                  key={i}
                  className="stagger-card-in w-full"
                  style={{ animationDelay: `${Math.min(i * 20, 300)}ms` }}
                >
                  {renderSkeleton ? (
                    renderSkeleton()
                  ) : (
                    <div className="skeleton h-[340px] w-full" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </ViewTransition>
      )}

      {isEmpty && (
        // Same surface boundary as the skeleton and the rows: without an enter
        // group this div lands in the `root` snapshot, painted underneath the
        // outgoing skeleton, which the hold keeps fully opaque — a zero-result
        // search read as a stalled skeleton for the whole hold plus fade.
        <ViewTransition
          enter="vt-surface-enter"
          exit="vt-surface-exit-hold"
          default="none"
        >
          <div className="flex h-full items-center justify-center p-2">
            <div className="text-center">
              <div className="text-lg font-semibold">{emptyMessage}</div>
              <p className="text-base-content/60">{emptyDescription}</p>
            </div>
          </div>
        </ViewTransition>
      )}

      {!showSkeleton && !isEmpty && (
        <ViewTransition
          key={`rows-${list.surfaceKey ?? "unkeyed"}`}
          enter="vt-surface-enter"
          exit="vt-surface-exit-hold"
          default="none"
        >
          <div
            ref={attachScrollContainer}
            inert={awaitingReplacementRows || undefined}
            // overscroll-y-contain: at the grid's current bottom mid-fetch (the
            // virtualizer only grows once the page lands) scroll chaining would
            // otherwise hand the gesture to the page, which lurches and has to
            // be scrolled back once the rows arrive. Y only: the shorthand
            // also pins the X axis, which is what the trackpad back-swipe
            // chains to.
            className="max-h-[100svh] flex-1 overflow-y-auto overflow-x-clip overscroll-y-contain py-2 pb-2 md:py-0 lg:pb-0"
          >
            <div
              style={{
                // No space reserved for a loading row: next-page fetches fire
                // before the user reaches the bottom (see the sentinel below),
                // and arriving cards animate in via <ViewTransition>. A
                // trailing skeleton needed manual height arithmetic here,
                // which is exactly what caused the container height to
                // oscillate on every page load.
                height: `${rowVirtualizer.getTotalSize()}px`,
                position: "relative",
                width: "100%",
              }}
            >
              {/* Sits at the very end of the list, outside the virtual window so
                it is never unmounted. Zero height: it marks the end, it does
                not occupy it. */}
              <div
                ref={loadMoreRef}
                aria-hidden
                style={{
                  position: "absolute",
                  top: rowVirtualizer.getTotalSize(),
                  height: 0,
                  width: "100%",
                }}
              />
              {(() => {
                // View-transition snapshots paint at the document root, NOT
                // clipped by this scroll container — so only rows FULLY inside
                // the visible window may animate. Overscan and edge-straddling
                // rows would otherwise paint ghost cards over the surrounding
                // layout on every re-key (facet/sort/search): even a row half
                // inside the box has a full-height snapshot that spills past
                // the container edge. Excluded rows swap instantly ("none").
                const viewTop = rowVirtualizer.scrollOffset ?? 0;
                const viewBottom = viewTop + (viewportHeight.current || 1000);
                // The end-of-list card lands with the final page, so it takes
                // the next beat after that page's last card — same batch, same
                // window, so a scroll that mounts it much later does not
                // replay. Resolved like any card rather than off the newest
                // batch: `.at(-1)` read whichever batch happened to be newest,
                // which after an in-place edit is one that carried the end card
                // over (dropping its entrance) or one whose `start` measures
                // the delay from the wrong origin.
                const endIndex = listData?.length ?? 0;
                const endBatch = staggerBatchFor(
                  list.stagger,
                  endIndex,
                  END_OF_LIST_KEY
                );
                const endOfListStagger = endBatch
                  ? {
                      animationDelay: `${Math.min((endIndex - endBatch.start) * 20, 600)}ms`,
                    }
                  : undefined;
                return rowVirtualizer.getVirtualItems().map((virtualRow) => {
                  const rowVisible =
                    virtualRow.start >= viewTop && virtualRow.end <= viewBottom;
                  if (virtualRow.index === endRowIndex) {
                    return (
                      <div
                        key="end-of-list"
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualRow.start}px)`,
                          gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                        }}
                        className="grid gap-2 px-1 pb-2"
                      >
                        <ViewTransition
                          name={vtName(END_OF_LIST_KEY)}
                          enter="none"
                          exit={rowVisible ? "vt-card-exit" : "none"}
                          default={rowVisible ? "vt-move" : "none"}
                        >
                          <div
                            className={cn(
                              "w-full",
                              endOfListStagger && "stagger-card-in"
                            )}
                            style={{
                              minHeight: measuredRowHeight.current,
                              ...endOfListStagger,
                            }}
                          >
                            <EndOfListCard
                              message={endOfListMessage}
                              description={endOfListDescription}
                            />
                          </div>
                        </ViewTransition>
                      </div>
                    );
                  }

                  const layoutIndex = toLayoutIndex(virtualRow.index);
                  const layoutRow = layoutRows[layoutIndex];
                  if (layoutRow.type === "header") {
                    return (
                      <div
                        key={layoutRow.key}
                        ref={rowVirtualizer.measureElement}
                        data-index={virtualRow.index}
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                        className="px-1"
                      >
                        {layoutRow.content}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={layoutRow.key}
                      ref={measureItemRow}
                      data-index={virtualRow.index}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                      }}
                      className="grid gap-2 px-1 pb-2"
                    >
                      {layoutRow.entries.map(({ item, index }) => {
                        const itemKey = getItemKey(item, index);
                        const stagger = staggerBatchFor(
                          list.stagger,
                          index,
                          itemKey
                        );
                        return (
                          // Explicit stable name so a card re-chunked into a
                          // different virtual row morphs to its new slot (share)
                          // instead of blinking out and back in.
                          <ViewTransition
                            key={itemKey}
                            name={vtName(itemKey)}
                            enter="none"
                            exit={rowVisible ? "vt-card-exit" : "none"}
                            default={rowVisible ? "vt-move" : "none"}
                          >
                            <div
                              className={cn(
                                "w-full min-w-0 hover:z-10",
                                stagger && "stagger-card-in"
                              )}
                              style={
                                stagger
                                  ? {
                                      // 20ms rather than 60 so a whole 30-card
                                      // page gets its own beat (29 * 20 = 580ms)
                                      // instead of the first 10 cascading and the
                                      // other 20 landing together on the cap.
                                      //
                                      // The 600ms ceiling stays: `both` keeps a
                                      // card invisible until its delay elapses, so
                                      // the delay is literally how long that slot
                                      // is a hole the user can scroll into — and
                                      // anything past the staggerBatchLive window
                                      // would not animate at all.
                                      animationDelay: `${Math.min((index - stagger.start) * 20, 600)}ms`,
                                    }
                                  : undefined
                              }
                            >
                              {renderItem(item, index, cols)}
                            </div>
                          </ViewTransition>
                        );
                      })}
                      {/* Same deal-in and cadence as the cards, so the skeletons
                        arrive as a continuation of the row rather than popping
                        in as a block. They cannot be staggered OUT: they are
                        removed in the same urgent commit that appends the cards
                        (no Transition, so <ViewTransition> never activates) and
                        they occupy the very cells those cards fill. The card
                        dealing in from opacity 0 over the vacated slot is what
                        covers the swap. */}
                      {layoutIndex === appendRowIndex &&
                        Array.from({ length: lastRowSkeletons }).map((_, i) => (
                          <div
                            key={`skeleton-${i}`}
                            className="stagger-card-in w-full"
                            style={{ animationDelay: `${i * 20}ms` }}
                          >
                            {renderSkeleton ? (
                              renderSkeleton()
                            ) : (
                              <CardSkeleton />
                            )}
                          </div>
                        ))}
                      {hasRoomInLastRow && layoutIndex === appendRowIndex && (
                        // Named like a card so it morphs to its new slot when
                        // it survives a re-key (browsing short lists) instead
                        // of dealing in from nothing each time.
                        <ViewTransition
                          name={vtName(END_OF_LIST_KEY)}
                          enter="none"
                          exit={rowVisible ? "vt-card-exit" : "none"}
                          default={rowVisible ? "vt-move" : "none"}
                        >
                          <div
                            className={cn(
                              "flex w-full",
                              endOfListStagger && "stagger-card-in"
                            )}
                            style={endOfListStagger}
                          >
                            <EndOfListCard
                              message={endOfListMessage}
                              description={endOfListDescription}
                            />
                          </div>
                        </ViewTransition>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
            {/* A full row, for when the last row was already full and there was no
              gap to top up. Flowed after the virtualized area rather than
              absolutely positioned inside it, so it extends the scroll height on
              its own — that is what avoids adding its height into the
              container's `height` above, the manual arithmetic that used to
              make the container oscillate on every page load. */}
            {needsSkeletonRow && (
              <div
                className="grid gap-2 px-1 pb-2"
                style={{
                  gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
                }}
              >
                {Array.from({ length: cols }).map((_, i) => (
                  <div
                    key={`skeleton-row-${i}`}
                    className="stagger-card-in w-full"
                    style={{ animationDelay: `${i * 20}ms` }}
                  >
                    {renderSkeleton ? renderSkeleton() : <CardSkeleton />}
                  </div>
                ))}
              </div>
            )}
          </div>
        </ViewTransition>
      )}
    </div>
  );
}
